import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useContext, useMemo } from 'preact/compat';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TextInput from '@widgets/shared/components/form-controls/TextInput';
import TextareaInput from '@widgets/shared/components/form-controls/TextareaInput';
import DatepickerInput from '@widgets/shared/components/form-controls/DatepickerInput';
import AmountSelector from '@voucherForm/components/AmountSelector';
import convertDataAttributes from '@widgets/shared/convertDataAttributes';
import { voucherFormSchema } from '@voucherForm/validation/schema';
import { ConfigCTX, configDefaults } from '@voucherForm/context/config';
import { useAppDispatch, useAppSelector } from '@voucherForm/store/hooks';
import { getQuickPayAppleState, getQuickPayGoogleState, setQuickPayState } from '@voucherForm/store/reducers/quickPay';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import { createRef, useCallback, useEffect, useState, } from 'react';
import { configureStore } from '@reduxjs/toolkit';
import { storeOptions } from '@genericBookings/store';
import { Provider } from 'react-redux';
import QuickPay from '@widgets/shared/components/QuickPay';
import PaymentIcons from '@basket/components/PaymentIcons';
import DiscountModal from '@voucherForm/components/DiscountModal';
import indepthDatepickerDay from '@genericBookings/components/IndepthDatepicker/Components/IndepthDatepickerDay/IndepthDatepickerDay';
import UsefulLinks from '@widgets/shared/components/UsefulLinks';
import Discount from '@voucherForm/components/Discount';
import { format } from 'date-fns';
import Notice from '@widgets/shared/components/Notice';
import useDidMount from 'beautiful-react-hooks/useDidMount';
function Form() {
    const termsRef = createRef();
    // TODO: modal and fix google/apple pay.
    const { action, hideQuickPayButtons, terms, modalTitle, modalDiscounts, usefulLinksTitle, usefulLinks, discountBands, paymentIcons, } = useContext(ConfigCTX);
    const [modalOpen, setModalOpen] = useState(modalTitle !== undefined && modalDiscounts !== undefined);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState(null);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(voucherFormSchema),
        defaultValues: {
            // acceptTerms: true,
            amount: 0,
        },
    });
    const { formState: { isValid, errors }, setValue, watch, } = form;
    const handleDidMount = useCallback(() => {
        window.dispatchEvent(new CustomEvent('primary.action.override', {
            detail: {
                buttonText: 'Book Now',
                buttonAction: () => {
                    const element = document.querySelector('.voucher-form .overview');
                    element?.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                    });
                },
                buttonEnabled: true,
            },
        }));
    }, []);
    useDidMount(handleDidMount);
    const shakeTerms = useCallback(() => {
        const el = termsRef.current;
        if (el) {
            el.addEventListener('animationend', () => {
                el.classList.remove('shake');
            });
            el.classList.add('shake');
        }
    }, [termsRef]);
    const dispatch = useAppDispatch();
    const appleHidden = useAppSelector(getQuickPayAppleState);
    const googleHidden = useAppSelector(getQuickPayGoogleState);
    const onAppleFailed = useGlobalEvent('apple.failed');
    const onGoogleFailed = useGlobalEvent('google.failed');
    const onPaymentSuccess = useGlobalEvent('payment.success');
    const onPaymentError = useGlobalEvent('payment.error');
    const handleAcceptedPaymentForms = useCallback(() => {
        // setValue('acceptTerms', (appleHidden && googleHidden) || hideQuickPayButtons, { shouldValidate: true });
    }, [appleHidden, googleHidden, hideQuickPayButtons, setValue]);
    const handleOpen = useCallback(() => setModalOpen(true), [setModalOpen]);
    const handleClose = useCallback(() => setModalOpen(false), [setModalOpen]);
    const handleErrorOpen = useCallback(() => setErrorModalOpen(true), [setErrorModalOpen]);
    const handleErrorClose = useCallback(() => setErrorModalOpen(false), [setErrorModalOpen]);
    useEffect(handleAcceptedPaymentForms, [appleHidden, googleHidden, hideQuickPayButtons, handleAcceptedPaymentForms]);
    onAppleFailed(() => dispatch(setQuickPayState('apple', true)));
    onGoogleFailed(() => dispatch(setQuickPayState('google', true)));
    onAppleFailed(() => dispatch(setQuickPayState('apple', true)));
    onGoogleFailed(() => dispatch(setQuickPayState('google', true)));
    onPaymentSuccess(() => setPaymentComplete(true));
    onPaymentError((event) => {
        setErrorModalContent(event.detail.errorMessage);
        // handleClose();
        handleErrorOpen();
    });
    const amount = watch('amount');
    const deliveryDate = watch('unformattedDeliveryDate');
    const quickPayVoucher = useMemo(() => ({
        type: 'voucher',
        amount,
    }), [amount]);
    const formattedPrice = useMemo(() => {
        if (amount !== null) {
            return `£${amount.toFixed(2)}`;
        }
        return undefined;
    }, [amount]);
    const formattedDate = useMemo(() => (deliveryDate ? format(new Date(deliveryDate), 'yyyy-MM-dd') : undefined), [deliveryDate]);
    const maybeDiscount = useMemo(() => discountBands
        .sort((bandA, bandB) => (bandA.min < bandB.min ? -1 : 1))
        .find((band) => amount >= band.min && amount < band.max), [discountBands, amount]);
    const onAppleAvailable = useGlobalEvent('apple.available');
    const onGoogleAvailable = useGlobalEvent('google.available');
    onAppleAvailable(() => setQuickPayState('apple', false));
    onGoogleAvailable(() => setQuickPayState('google', false));
    return (_jsxs(FormProvider, { ...form, children: [_jsx("form", { action: action, className: "voucher-form", method: "POST", noValidate: true, children: _jsxs("div", { className: "rfx-grid padding-large basket", children: [_jsx("div", { className: "rfx-col-md-7", children: _jsxs("div", { className: "form__section", children: [_jsx("div", { className: "section__title", children: _jsx("span", { className: "h3", children: "Voucher Amount" }) }), _jsx(AmountSelector, { name: "amount", labelAttrs: {
                                            text: 'Please select an amount',
                                            hidden: true,
                                        }, options: [
                                            {
                                                value: '20',
                                                label: '£20',
                                            },
                                            {
                                                value: '50',
                                                label: '£50',
                                            },
                                            {
                                                value: 'other',
                                                label: 'Other',
                                            },
                                        ] }), _jsx(TextInput, { inputAttrs: {
                                            id: 'voucher-to',
                                            name: 'to',
                                            type: 'email',
                                            placeholder: 'Recipient email',
                                            required: true,
                                        }, labelAttrs: {
                                            text: 'To',
                                            isFieldRequired: true,
                                        } }), _jsx(TextInput, { inputAttrs: {
                                            id: 'voucher-from',
                                            name: 'from',
                                            placeholder: 'Your name',
                                            type: 'text',
                                            required: true,
                                        }, labelAttrs: {
                                            text: 'From',
                                            isFieldRequired: true,
                                        } }), _jsx(DatepickerInput, { inputAttrs: {
                                            id: 'voucher-delivery-date',
                                            name: 'unformattedDeliveryDate',
                                            minDate: new Date(),
                                            inline: false,
                                            isClearable: false,
                                            showPopperArrow: true,
                                            renderDayContents: indepthDatepickerDay,
                                            shouldCloseOnSelect: true,
                                        }, labelAttrs: { text: 'Delivery Date' } }), _jsx("input", { type: "hidden", name: "deliveryDate", value: formattedDate }), _jsx(TextareaInput, { inputAttrs: {
                                            id: 'voucher-message',
                                            name: 'message',
                                            placeholder: 'Please enter your message',
                                            rows: 12,
                                        }, labelAttrs: { text: 'Message' } })] }) }), _jsxs("div", { className: "rfx-col-md-5", children: [_jsxs("div", { className: "overview", children: [formattedPrice && (_jsx("span", { className: "overview__total", children: formattedPrice })), _jsx("div", { className: "overview__btns", children: _jsx(QuickPay, { onClickedWhenDisabled: () => { }, googleHidden: googleHidden, appleHidden: appleHidden, termsAccepted: isValid, products: quickPayVoucher, handleGoogleDidFail: (status) => dispatch(setQuickPayState('google', status)), handleAppleDidFail: (status) => dispatch(setQuickPayState('apple', status)) }) }), _jsx("button", { disabled: !isValid, type: "submit", className: "btn overview__checkout", children: "Checkout" })] }), maybeDiscount && (_jsx(Discount, { ...maybeDiscount })), _jsx("div", { className: "pay-later", children: _jsx("div", { className: "pay-later__terms", children: _jsxs("span", { className: "p-med", children: ["By proceeding you agree to the\u00A0", _jsx("a", { href: "#", children: "Terms & Conditions" })] }) }) }), paymentIcons && (_jsx(PaymentIcons, { icons: paymentIcons })), _jsx(UsefulLinks, { title: usefulLinksTitle, usefulLinks: usefulLinks })] })] }) }), modalOpen && modalTitle && modalDiscounts && (_jsx(DiscountModal, { modalTitle: modalTitle, modalDiscounts: modalDiscounts, onClose: () => setModalOpen(false) })), (errorModalOpen && errorModalContent) && (_jsx(Notice, { onClose: handleErrorClose, content: errorModalContent }))] }));
}
export default function VoucherForm(props) {
    const { dataset } = props;
    const store = configureStore(storeOptions); // done for store per instance reasons
    const config = convertDataAttributes(dataset ?? {});
    return (_jsx(ConfigCTX.Provider, { value: { ...configDefaults, ...config }, children: _jsx(Provider, { store: store, children: _jsx(Form, {}) }) }));
}
